export interface CompositionEdit {
    old: string;
    new: string;
}

interface EditError {
    index: number;
    reason: string;
    context?: string;
}

export interface ApplyEditsResult {
    html: string;
    // Indices (0-based) of edits skipped because old === new. A no-op is
    // harmless, so it's skipped with a note rather than failing the whole batch.
    skipped: number[];
    // Indices of edits whose `old` was already consumed by an EARLIER edit in the
    // same batch (gone from the working buffer, but it uniquely matched the original).
    // Such an edit is redundant rather than wrong, so it's skipped with a note and the
    // batch still succeeds — instead of an opaque atomic failure that burns a turn.
    superseded: number[];
    // EVERY edit that failed to match, not just the first. The whole batch still
    // commits nothing while this is non-empty (the tool wrapper enforces atomicity);
    // collecting all failures lets the agent fix them in one resend instead of
    // discovering them one rejected call at a time.
    errors: EditError[];
}

// How much text to echo on each side of the divergence point.
const DIVERGENCE_CONTEXT_RADIUS = 120;
// How much of the matched prefix to echo before the divergence, for orientation. Only the tail:
// echoing a long matched prefix in full is what buries the boundary the agent needs to see.
const MATCHED_PREFIX_TAIL = 60;

/** The tail of `text`, marked with a leading ellipsis when it was cut. */
function tail(text: string, max: number): string {
    return text.length > max ? `…${text.slice(-max)}` : text;
}

/** The head of `text`, marked with a trailing ellipsis when it was cut. */
function head(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max)}…` : text;
}

/**
 * When an edit's `old` isn't found, locate the longest prefix of `old` that still exists in the
 * document and show that boundary from BOTH sides — what the composition has there, and what the
 * agent's `old` expected. "Is a substring" is monotonic in prefix length (a shorter prefix of a
 * matching prefix also matches), so binary-search the boundary.
 *
 * Both sides matter: given only the document's text the agent has to guess which of its own
 * characters was wrong, and a wrong guess costs a whole turn (the batch is atomic, so a single
 * unmatched edit means resending all of them). Each side is echoed as the shared prefix's tail plus
 * its own continuation, so the two read as aligned strings that visibly part company.
 */
function divergenceContext(html: string, old: string): string | undefined {
    let lo = 0;
    let hi = old.length;
    while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (html.includes(old.slice(0, mid))) lo = mid;
        else hi = mid - 1;
    }
    if (lo === 0) return undefined; // nothing in common — no useful anchor
    const at = html.indexOf(old.slice(0, lo));
    const shared = tail(old.slice(0, lo), MATCHED_PREFIX_TAIL);
    const inComposition = head(html.slice(at + lo), DIVERGENCE_CONTEXT_RADIUS);
    const inOld = head(old.slice(lo), DIVERGENCE_CONTEXT_RADIUS);
    return (
        `Your \`old\` matches the composition for its first ${lo} char(s), then diverges. ` +
        `The two read identically up to the divergence:\n` +
        `--- the composition has ---\n${shared}${inComposition}\n` +
        `--- your \`old\` has ---\n${shared}${inOld}`
    );
}

interface Match {
    index: number;
    length: number;
}

const REGEX_META = /[.*+?^${}()|[\]\\]/g;

// A "soft break" between tokens: a run of whitespace and/or <br> tags (<br>, <br/>, <br />).
// Treating <br> like whitespace lets an authored `old` that used a line break match stored markup
// that used <br> (or vice versa) — after several edits the agent commonly misremembers one as the
// other, which used to be an opaque "not found".
const SOFT_BREAK = '(?:\\s|<br\\s*/?>)';
const SOFT_BREAK_SPLIT = new RegExp(`${SOFT_BREAK}+`);

/**
 * Break-tolerant matches of `old` in `html`: a run of whitespace and/or `<br>` tags in `old`
 * matches any such run in the document. This lets the agent paste the HTML it authored — where
 * attributes are split across lines, or a `<br>` is remembered as a newline — and still match the
 * stored single-line canonical form, without a read first.
 *
 * Only used as a fallback when an exact match fails, so it never loosens an unambiguous exact hit
 * (and the result must still be unique). Script/style text is preserved verbatim, so JS edits match
 * exactly; this only bridges the inter-token breaks the serialization collapses.
 */
function whitespaceTolerantMatches(html: string, old: string): Match[] {
    const tokens = old.split(SOFT_BREAK_SPLIT).filter((t) => t.length > 0);
    if (tokens.length === 0) return []; // break-only old — no meaningful anchor
    const pattern = tokens.map((t) => t.replace(REGEX_META, '\\$&')).join(`${SOFT_BREAK}+`);
    const re = new RegExp(pattern, 'g');
    const matches: Match[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(html)) !== null) {
        matches.push({ index: m.index, length: m[0].length });
        if (m.index === re.lastIndex) re.lastIndex++; // guard against zero-length loops
    }
    return matches;
}

// Locate the single span `old` should replace. Exact match wins; on a miss we
// fall back to a whitespace-tolerant match so authored multi-line HTML still
// resolves against the canonical store. Returns a Match, or a reason string.
function findUniqueMatch(html: string, old: string): Match | { reason: string } {
    if (old === '') {
        // indexOf('') is 0 and would report "matches multiple" — nonsensical advice.
        // An empty `old` has no anchor to locate, so reject it with a real reason.
        return {
            reason: '`old` is empty, so it has nothing to locate. Provide the exact substring you want to replace (to insert, include the surrounding markup plus your addition).',
        };
    }
    const firstIndex = html.indexOf(old);
    if (firstIndex !== -1) {
        if (html.indexOf(old, firstIndex + old.length) !== -1) {
            return {
                reason: '`old` matches multiple places in the composition. Add more surrounding text to make the match unique.',
            };
        }
        return { index: firstIndex, length: old.length };
    }
    const fuzzy = whitespaceTolerantMatches(html, old);
    if (fuzzy.length === 1) return fuzzy[0];
    if (fuzzy.length > 1) {
        return {
            reason: '`old` matches multiple places in the composition (ignoring whitespace). Add more surrounding text to make the match unique.',
        };
    }
    return {
        // Be precise about what the tolerant fallback actually covers: it collapses runs of
        // whitespace and `<br>` BETWEEN tokens, so a space inside a quoted string or a text node is
        // still a hard mismatch. Saying "whitespace is tolerated" sends the agent hunting for an
        // exotic cause when a stray space in its own copy is the usual culprit.
        // The tolerance is positional, not structural: `old` is split on whitespace runs and
        // rejoined with SOFT_BREAK+, so a run matches a run anywhere — including inside text nodes
        // and quoted strings. Only whitespace against *no* whitespace shifts token boundaries.
        reason: `\`old\` not found in the composition. A run of whitespace/\`<br>\` matches any other run where it separates two tokens; adding or removing whitespace where the other side has none is a hard mismatch.`,
    };
}

export function applyEdits(initialHtml: string, edits: CompositionEdit[]): ApplyEditsResult {
    let html = initialHtml;
    const skipped: number[] = [];
    const superseded: number[] = [];
    const errors: EditError[] = [];
    for (let i = 0; i < edits.length; i++) {
        const { old: oldText, new: newText } = edits[i];
        if (oldText === newText) {
            skipped.push(i);
            continue;
        }
        const match = findUniqueMatch(html, oldText);
        if ('reason' in match) {
            // Superseded-edit detection: `old` is gone from the working buffer, but if it uniquely
            // matched the ORIGINAL document then an earlier edit in this batch already changed that
            // region — this edit is redundant, not wrong. Skip it (the batch still succeeds) rather
            // than failing atomically. A genuine miss (absent from the original too, or i === 0)
            // is a real failure.
            const notFound = /not found/.test(match.reason);
            if (notFound && i > 0 && !('reason' in findUniqueMatch(initialHtml, oldText))) {
                superseded.push(i);
                continue;
            }
            // Record the failure but keep validating the rest of the batch so the agent
            // sees EVERY broken edit at once. Nothing is committed while `errors` is
            // non-empty (the tool wrapper enforces atomicity), so we simply leave this
            // edit unapplied and let later edits validate against the buffer as-is.
            errors.push({
                index: i,
                reason: match.reason,
                context: notFound ? divergenceContext(html, oldText) : undefined,
            });
            continue;
        }
        html = html.slice(0, match.index) + newText + html.slice(match.index + match.length);
    }
    return { html, skipped, superseded, errors };
}
